import { FC, useEffect, useRef, useState } from 'react'
import { useLayout } from '../../../_metronic/layout/core'
import { useHistory, useParams } from 'react-router-dom'
import { ExperienceDetail } from '../teamExperiences/models/ExperienceSummaryModel'
import { getDetailExperience } from '../../services/teamExperienceService'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Step1 } from './components/steps/Step1'
import { RequiredInfoModal } from '../../helpers/RequiredInfoModal'
import { createIntent, preValidateBooking } from '../../services/bookingService'
import { useSelector, shallowEqual } from 'react-redux'
import { showErrorAlert } from '../../helpers/alert'
import { useConfirmAction } from '../../hooks/utils/useConfirmAction'
import { PaymentMethodModel } from '../paymentMethod/components/PaymentMethodModel'

const ExperienceBookingPage: FC = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const history = useHistory()
    const { id } = useParams<{ id: string }>()
    const formikRef = useRef<any>();
    const [ experience, setExperience ] = useState<ExperienceDetail>({});
    const [ step ] = useState(1)
    const [showModal, setShowModal] = useState(false);
    const [messageModal, setMessageModal] = useState("");
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [isSubmittingBooking, setIsSubmittingBooking] = useState(false);
    const MaxGuests: number = 10
    const user = useSelector((state: any) => state.auth?.user, shallowEqual)
    const confirm = useConfirmAction()

    useEffect(() => {
        if (!id) {
            history.goBack();
            return;
        }
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: false },
            toolbar: { ...config.toolbar, display: false },
            aside: { ...config.aside, display: false },
            footer: { ...config.footer, display: true },
            main: {
                ...config.main,
                body: { ...config.main?.body, class: 'app-blank' },
            },
        })
        document.body.classList.add('bg-experience-preview');
        document.getElementById('kt_footer')?.classList.add('bg-experience-preview');

        const fetchData = async () => {
            const experience_ = await getDetailExperience(Number(id));
            if (experience_?.id) {
                await setExperience(experience_);
                if(experience_?.dates?.length === 1){
                    let dateId = experience_?.dates[0]?.id;
                    formikRef.current?.setFieldValue('date_id', dateId)
                }
            } else {
                history.goBack();
            }
        }
        fetchData();
    }, [])

    const initialValues = {
        experience_id: 0,
        topic: "",
        message: "",
        audience_type: 1,
        guest_capacity_adults: 1,
        guest_capacity_children: 0,
        guest_capacity_youth: 0,
        guest_capacity_infants: 0,
        enhancements_data: [],
        guests: [],
        subtotal: 0,
        tax: 0,
        total: 0,
        term_condition: false,
        attendees: [],
        date_id: '',
        payment_intent: '',
        total_amount: 0,
    };
    
    const stepFields = {
        1: ["topic", "message", "term_condition", "date_id"]
    };

    const onNextStep = async () => {
        let error = await haveErrors();
        if (!error) {
            const values = formikRef.current.values;
            if(!values.date_id){
                showErrorAlert({title:'Notice',message:'Please select a date'})
                return;
            }

            const adultsNeeded = Math.max(0, Number(values.guest_capacity_adults || 1) - 1);
            const youthNeeded = Number(values.guest_capacity_youth || 0);
            const childrenNeeded = Number(values.guest_capacity_children || 0);
            const infantsNeeded = Number(values.guest_capacity_infants || 0);
            const totalRelativesNeeded = adultsNeeded + youthNeeded + childrenNeeded + infantsNeeded;

            if (totalRelativesNeeded > 0 && (values.guests || []).length !== totalRelativesNeeded) {
                setMessageModal(`Please select ${totalRelativesNeeded} relative(s) from your profile to match your selected attendees.`);
                setShowModal(true);
                return;
            }

            // 1. Preguntar confirmación sobre uso del método de pago primario y retención temporal
            const totalFormatted = Number(values.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const confirmed = await confirm({
                title: 'Confirm Booking Request',
                message: `We will use your primary payment method to place a temporary hold of $${totalFormatted} for this booking. Funds will only be captured if the host accepts your request. Would you like to proceed?`,
                btnTextAction: 'Authorize & Request',
                btnTextDecline: 'Cancel'
            });

            if (!confirmed) return;

            // 2. Pre-validamos la capacidad y el estado de la reserva antes de enviar
            const params = {
                experience_id: experience.id,
                date_id: values.date_id,
                guest_capacity_adults: values.guest_capacity_adults,
                guest_capacity_children: values.guest_capacity_children,
                guest_capacity_youth: values.guest_capacity_youth,
                guest_capacity_infants: values.guest_capacity_infants,
                user_id: user.id
            };

            const validation = await preValidateBooking(params);
            if (validation) {
                const { status: valStatus, message: valMessage } = validation;

                if (valStatus === "insufficient_spots") {
                    setMessageModal(valMessage || "Only a few spots left for this experience.");
                    setShowModal(true);
                    return;
                } else if (valStatus === "full") {
                    setMessageModal(valMessage || "There are no spots left for this experience. Please try another date or wait for upcoming dates.");
                    setShowModal(true);
                    return;
                } else if (valStatus === "confirmed") {
                    setMessageModal(valMessage || "You already have a confirmed booking for this date. Please select another date if you need to make another reservation.");
                    setShowModal(true);
                    return;
                }

                // 3. Validar si el usuario tiene método de pago registrado
                const hasPayment = validation?.hasPaymentMethod ?? validation?.has_payment_method;
                if (!hasPayment) {
                    setShowPaymentModal(true);
                    return;
                }
            }

            formikRef.current.submitForm();
        }
    }

    const haveErrors = async () => {
        const fieldsToValidate = stepFields[step] || [];
        const errors = await formikRef.current.validateForm();
        const stepErrors = Object.keys(errors).filter((key) =>
            fieldsToValidate.includes(key)
        );
        if(stepErrors.length>0){
            setMessageModal(`Please enter '${String(stepErrors[0]).replaceAll('_',' ')}' to save your progress.`);
            let input = document.querySelector(`input[name='${stepErrors[0]}']`);
            if(!input){
                input = document.querySelector(`textarea[name='${stepErrors[0]}']`);
            }
            if(!input){
                input = document.querySelector(`select[name='${stepErrors[0]}']`);
            }
            input?.focus();
            setShowModal(true);
            setTouchedForStep();
            return true;
        }
        return false;
    }

    const setTouchedForStep = () => {
        const fieldsToValidate = stepFields[step] || [];
        let newTouched: any = {};
        for (const field of fieldsToValidate) {
            const value = formikRef.current.values[field];
            if (!Array.isArray(value)) {
                newTouched[field] = true;
                continue;
            }
            if (Array.isArray(value)) {
                newTouched[field] = value.map((item) => {
                    if (typeof item === "object" && item !== null) {
                        return Object.fromEntries(
                            Object.keys(item).map((key) => [key, true])
                        );
                    }
                    return true;
                });
            }
        }
        formikRef.current.setTouched({
            ...formikRef.current.touched,
            ...newTouched,
        });
    };

    const validationSchema = Yup.object({
        enhancements_data: Yup.array().of(
            Yup.object().shape({
                id: Yup.number().required("Service price is required"),
            })
        ),
        guests: Yup.array().of(Yup.number()).notRequired(),
        topic: Yup.string()
            .max(100, "Max 100 characters")
            .when("guests", {
                is: (guests: number[] | undefined) => guests && guests.length > 0,
                then: (schema) => schema.required("Topic is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        message: Yup.string()
            .max(100, "Max 100 characters")
            .when("guests", {
                is: (guests: number[] | undefined) => guests && guests.length > 0,
                then: (schema) => schema.required("Message is required"),
                otherwise: (schema) => schema.notRequired(),
            }),
        audience_type: Yup.number(),
        guest_capacity_adults: Yup.number().typeError("Must be a number").integer("Must be an integer").min(0, "Must be 0 or greater").max(MaxGuests, `Please enter a quantity less than ${MaxGuests}`).required("Capacity adult is required"),
        guest_capacity_children: Yup.number().typeError("Must be a number").integer("Must be an integer").min(0, "Must be 0 or greater").max(MaxGuests, `Please enter a quantity less than ${MaxGuests}`).required("Capacity children is required"),
        guest_capacity_youth: Yup.number().typeError("Must be a number").integer("Must be an integer").min(0, "Must be 0 or greater").max(MaxGuests, `Please enter a quantity less than ${MaxGuests}`).required("Capacity youth is required"),
        guest_capacity_infants: Yup.number().typeError("Must be a number").integer("Must be an integer").min(0, "Must be 0 or greater").max(MaxGuests, `Please enter a quantity less than ${MaxGuests}`).required("Capacity infants is required"),
        subtotal: Yup.number(),
        tax: Yup.number(),
        total: Yup.number(),
        term_condition: Yup.boolean().oneOf([true], "You must agree to the terms and conditions"),
        guests_count: Yup.number(),
        attendees: Yup.array().notRequired(),
        date_id: Yup.number().required('Event date is required'),
        payment_intent: Yup.string(),
        total_amount: Yup.number(),
    });

    const handleSubmit = async (values: any) => {
        setIsSubmittingBooking(true);
        let data = JSON.parse(JSON.stringify(values));
        delete data.audience_type;
        delete data.enhancements_data;
        delete data.subtotal;
        delete data.tax;
        delete data.total;
        delete data.term_condition;
        data.enhancement_ids = (values?.enhancements_data || []).map((e: any) => e.id);

        try {
            let res = await createIntent(data);
            if (res && (res.bookingId || res.booking_id || res.status === 'pending' || res.status === 'confirmed')) {
                history.push(`/experience/booking/${experience?.id}/success`);
            } else if (res && res.error) {
                showErrorAlert({ title: 'Booking Failed', message: res.error });
            }
        } catch (err: any) {
            showErrorAlert({ title: 'Booking Failed', message: err?.message || 'Failed to complete booking authorization.' });
        } finally {
            setIsSubmittingBooking(false);
        }
    }

    const handlePaymentModalSubmit = () => {
        setShowPaymentModal(false);
        formikRef.current?.submitForm();
    }

    return (<>
        <div className="request-page">
            <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} validateOnMount={true} enableReinitialize={false} onSubmit={handleSubmit}>
                {(formik) => {
                    return (
                        <form className="gap-80px d-flex flex-col w-100" autoComplete="off" onSubmit={formik.handleSubmit}>
                            <Step1 experience={experience} step={step} onNextStep={onNextStep} maxGuests={MaxGuests} />
                        </form>
                    )
                }}
            </Formik>
            <RequiredInfoModal open={showModal} message={messageModal} onClose={() => setShowModal(false)} />
            <PaymentMethodModel
                show={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                onSubmit={handlePaymentModalSubmit}
            />
        </div>
    </>);
}
export { ExperienceBookingPage }